<template>
  <div class="cvcql-date-time-inputs">
    <v-menu
      v-if="!readonly"
      v-model="dateMenu"
      :close-on-content-click="false"
      transition="scale-transition"
      offset-y
    >
      <template v-slot:activator="{ on, attrs }">
        <div class="d-inline-flex">
          <v-icon class="mx-3" :style="iconStyles">
            mdi-calendar
          </v-icon>
          <v-text-field
            readonly
            dense hide-details
            single-line
            :value="dateValue"
            prepend-icon=""
            v-bind="attrs"
            v-on="on"
            :height="height ? height + 'px' : null"
            :style="width ? { width: width + 'px' } : {}"
            :clearable="clearable"
            :error-messages="errorMessages"
            @click:clear="onClearDate"
          />
        </div>
      </template>
      <v-date-picker
        :first-day-of-week="1"
        v-model="dateValue"
        color="colorm"
        :allowed-dates="allowedDates"
      />
    </v-menu>

    <span v-else>
      {{ dateValue }}
    </span>
  </div>
</template>

<script>
import {
  parseISO,
  format,
  startOfISOWeek,
  addDays,
  getISOWeek,
  isSameDay,
} from "date-fns";

export default {
  name: "CvcDateInput",
  data: () => ({
    parseISO,
    getISOWeek,
    dateValue: null,
    dateMenu: false,
    iconHeight: 20,
    height: 14,
    width: 150,
  }),
  props: {
    value: {
      type: String,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    initialDatetime: {
      type: Number || Date,
      required: false,
      default: null,
    },
    onChange: {
      type: Function,
      required: false,
      default: () => {},
    },
    allowedDates: {
      type: Function,
      default: () => true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    errorMessages: {
      type: Array,
      default: () => [],
    },
  },
  created() {
    if (this.initialDatetime) {
      this.dateValue = format(this.initialDatetime, "yyyy-MM-dd");
    }
  },
  computed: {
    iconStyles() {
      let ret = {};
      if (this.iconHeight) {
        ret["font-size"] = this.iconHeight + "px";
      }
      if (this.height) {
        ret["margin-top"] =
          (this.height + 8 - (this.iconHeight || 24)) / 2 + "px";
      }
      return ret;
    },
    datetime() {
      if (this.dateValue) {
        return parseISO(this.dateValue);
      } else {
        return null;
      }
    },
    dateWeek: {
      get() {
        if (!this.datetime) return [];
        const weekStart = startOfISOWeek(this.datetime);
        return new Array(7)
          .fill(0)
          .map((v, i) => format(addDays(weekStart, i), "yyyy-MM-dd"));
      },
      set(val) {
        if (val.length === 0) this.dateValue = null;
        this.dateValue = val[val.length - 1];
      },
    },
    dateWeekFormatted() {
      if (!this.dateValue) return "";
      const date = parseISO(this.dateValue);
      return `Week ${getISOWeek(date)}, ${date.getFullYear()}`;
    },
  },
  methods: {
    onChangeDate() {
      this.dateMenu = false;
      this.onChange(this.datetime);
    },
    onClearDate() {
      this.dateValue = null;
    },
  },
  watch: {
    dateValue(newVal) {
      this.onChangeDate();
      this.$emit("input", newVal);
    },
    value: {
      handler(newVal) {
        // Only ISO dates are allowed as a value, so we do some basic sanitising of input
        if(newVal?.length ?? 0 > 10) newVal = newVal.slice(0, 10)

        this.dateValue = newVal;
      },
      immediate: true,
    },
    initialDatetime(newVal, oldVal) {
      if (process.env.NODE_ENV !== "production")
        console.log(
          "Use of initialDatetime is deprecated, please use v-model API instead."
        );
      if (newVal && (!oldVal || !isSameDay(newVal, oldVal))) {
        this.dateValue = format(newVal, "yyyy-MM-dd");
      }
    },
  },
};
</script>

<style lang="scss">
.week-date-picker {
  tr {
    &:hover {
      background-color: var(--v-colore-base);
    }
  }
}
</style>
